import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Minus, Plus } from "lucide-react";
import { formatMoney } from "../lib/format";

// Mirrors MAX_ORDER_QUANTITY in backend/app/services/cafe.py — a typo guard, not a stock rule.
export const MAX_ORDER_QUANTITY = 99;

// The counts people actually ask for: a pair, a round for the table, a carton.
const PRESETS = [2, 3, 5, 10];

const clamp = (n) => Math.min(MAX_ORDER_QUANTITY, Math.max(1, n));

/**
 * How many of one item — used wherever a sale can be more than a single tap
 * (the Cafe counter and a table's tab). The unit price is passed in already
 * night-adjusted, so the total shown here is the total that gets charged.
 */
export default function QuantityDialog({
  open,
  onOpenChange,
  item,
  unitPrice = 0,
  unitProfit = null,   // admin only; null hides the profit line
  title,
  description,
  confirmLabel = "Record Sale",
  busyLabel = "Recording...",
  busy = false,
  onConfirm,
}) {
  // Kept as text so the field can be cleared and retyped; the number is derived.
  const [raw, setRaw] = useState("1");
  const quantity = parseInt(raw, 10);
  const valid = Number.isFinite(quantity) && quantity >= 1 && quantity <= MAX_ORDER_QUANTITY;

  // Every open starts at one, so a previous bulk sale can't be repeated by accident.
  useEffect(() => { if (open) setRaw("1"); }, [open]);

  const step = (delta) => setRaw(String(clamp((valid ? quantity : 1) + delta)));

  const submit = () => { if (valid && !busy) onConfirm(quantity); };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <DialogContent className="bg-[#18181b] border-zinc-700 text-zinc-100 max-w-sm mx-4" data-testid="quantity-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            {title || `Sell ${item?.name || ""}`}
          </DialogTitle>
          {description && <DialogDescription className="text-zinc-400">{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => step(-1)}
              disabled={valid && quantity <= 1}
              className="h-12 w-12 p-0 border-zinc-700 text-zinc-200 shrink-0"
              data-testid="qty-minus"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <Input
              type="number"
              inputMode="numeric"
              min="1"
              max={MAX_ORDER_QUANTITY}
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              // Leaving the field settles it: blank becomes 1, 150 becomes 99
              onBlur={() => setRaw(String(Number.isFinite(quantity) ? clamp(quantity) : 1))}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              className="h-14 w-24 text-center font-heading text-3xl font-bold bg-[#09090b] border-zinc-700 text-zinc-100"
              data-testid="qty-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => step(1)}
              disabled={valid && quantity >= MAX_ORDER_QUANTITY}
              className="h-12 w-12 p-0 border-zinc-700 text-zinc-200 shrink-0"
              data-testid="qty-plus"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex items-center justify-center gap-2">
            {PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRaw(String(n))}
                data-testid={`qty-preset-${n}`}
                className={`h-9 min-w-12 px-3 rounded-lg text-sm font-semibold transition-colors duration-150 ${
                  quantity === n
                    ? "bg-green-600 text-white"
                    : "bg-[#09090b] border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          <div className="border-t border-zinc-700 pt-4 space-y-1">
            {/* Nothing is charged when there is no price — house use says so
                rather than showing a ₹0 total. */}
            {unitPrice > 0 ? (
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">
                ₹{formatMoney(unitPrice)} each × {valid ? quantity : "—"}
              </span>
              <span className="text-2xl font-heading font-bold text-green-400" data-testid="qty-total">
                ₹{formatMoney(valid ? unitPrice * quantity : 0)}
              </span>
            </div>
            ) : (
              <div className="text-sm text-zinc-400 text-center" data-testid="qty-no-charge">
                {valid ? quantity : "—"} off the shelf · no charge
              </div>
            )}
            {unitProfit != null && (
              <div className="flex items-center justify-between text-xs text-zinc-500">
                <span>Profit</span>
                <span className="text-green-400">+₹{formatMoney(valid ? unitProfit * quantity : 0)}</span>
              </div>
            )}
            {!valid && (
              <p className="text-xs text-red-400">Enter a number between 1 and {MAX_ORDER_QUANTITY}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="qty-confirm"
            onClick={submit}
            disabled={busy || !valid}
            className="w-full h-12 text-lg font-heading font-bold bg-green-600 hover:bg-green-700 text-white rounded-lg"
          >
            {busy ? busyLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
